import React, { useRef, useState, useEffect, useCallback } from "react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGetPageContext } from "@/context/PageContext";
import Topbar from "@/components/Topbar";
import TotoAvatar from "@/components/TotoAvatar";
import { Send, Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Ask SONMI — chat composer that also accepts narrative evidence.
 *
 * Two lawful things happen here:
 *
 *  1. A conversational reply (streamed from /api/ai/chat).
 *  2. Artifact attachment — a file the reviewer wants SONMI to see.
 *     Files are stored under a per-user "Ask SONMI · chat artifacts"
 *     Workspace session so provenance is preserved. Once attached, the
 *     reviewer may share the artifact with a Vessel (currently: Payroll
 *     Vessel) — which routes it into the concept-review pipeline via
 *     /api/worlds/{world_id}/observe.
 *
 * IMPORTANT boundary: files uploaded here are *narrative evidence*
 * (see /app/CANON/EVIDENCE.md). They are never parsed as certified
 * payroll handoffs and never create payroll_entries. A Markdown
 * Understanding document is Canon-shaping evidence — it belongs in
 * the World, not in the ledger.
 */

const SUGGESTIONS = [
  "Draft a warm thank-you note to my team for the weekend rush.",
  "How do I run a good 30-day check-in?",
  "Summarize this week's district pulse in three sentences.",
  "Give me talking points for a 1:1 with a therapist who feels burned out.",
];

const ACCEPTED_EXTS = [".md", ".pdf", ".docx", ".csv", ".xlsx"];
const CHAT_SESSION_TITLE = "Ask SONMI · chat artifacts";

function fileTypeLabel(filename) {
  const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
  const m = { md: "Markdown", pdf: "PDF", docx: "Word document", csv: "CSV", xlsx: "Excel workbook" };
  return m[ext] || "Document";
}

function detectReaction(text) {
  if (!text) return "thinking";
  const t = text.toLowerCase();
  if (/(congrat|amazing|wonderful|great job|excellent|well done|beautiful|proud|love this|nailed it|celebrat|approved|exported|milestone|success|🎉)/.test(t)) return "celebrating";
  if (/(warning|careful|watch out|attention|serious|urgent|issue|problem|error|flag|concern|below minimum)/.test(t)) return "alert";
  if (/(let me help|here'?s how|you can|try this|walk you through|step by step|start with|first,|next,|then,)/.test(t)) return "helping";
  if (text.trim().endsWith("?")) return "curious";
  return "happy";
}

async function ensureChatSession() {
  // Look for an existing "Ask SONMI · chat artifacts" session, else create one.
  try {
    const r = await api.get("/workspace/sessions");
    const list = r.data || [];
    const found = list.find((s) => s.title === CHAT_SESSION_TITLE);
    if (found) return found.id;
  } catch (e) {}
  const created = await api.post("/workspace/sessions", { title: CHAT_SESSION_TITLE });
  return created.data.id;
}

async function findPayrollVessel() {
  try {
    const r = await api.get("/vessels");
    const vs = r.data || [];
    return vs.find((v) => (v.world_name || "").toLowerCase() === "payroll") || null;
  } catch (e) { return null; }
}

export default function TotoChat() {
  const { user } = useAuth();
  const nav = useNavigate();
  const getPageContext = useGetPageContext();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [reactionSettled, setReactionSettled] = useState({});
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  // Attachment currently pinned to the composer.
  const [pending, setPending] = useState(null);  // { artifact, purpose, sessionId }
  const [uploading, setUploading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [purpose, setPurpose] = useState("");

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    if (streaming) return;
    if (messages.length === 0) return;
    const lastIdx = messages.length - 1;
    if (messages[lastIdx].role !== "assistant") return;
    if (reactionSettled[lastIdx]) return;
    const t = setTimeout(() => setReactionSettled((s) => ({ ...s, [lastIdx]: true })), 4000);
    return () => clearTimeout(t);
  }, [streaming, messages, reactionSettled]);

  const stateFor = (m, i) => {
    if (m.role !== "assistant") return "idle";
    if (streaming && i === messages.length - 1) return "thinking";
    if (i === messages.length - 1 && !reactionSettled[i]) return detectReaction(m.text);
    return "happy";
  };

  const onAttachClick = () => fileInputRef.current?.click();
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error(`Only ${ACCEPTED_EXTS.join(", ")} files can be attached.`);
      return;
    }
    setUploading(true);
    try {
      const sessionId = await ensureChatSession();
      const fd = new FormData();
      fd.append("files", file);
      const up = await api.post(
        `/workspace/sessions/${sessionId}/artifacts/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const arts = up.data || [];
      if (!arts.length) throw new Error("no artifact returned");
      setPending({ artifact: arts[arts.length - 1], sessionId });
      setPurpose("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Attach failed.");
    } finally {
      setUploading(false);
    }
  };

  const clearAttachment = () => { setPending(null); setPurpose(""); };

  const shareWithPayrollVessel = async () => {
    if (!pending) return;
    setSharing(true);
    try {
      const vessel = await findPayrollVessel();
      if (!vessel) {
        toast.error("Payroll Vessel not found for your user.");
        setSharing(false);
        return;
      }
      // Preserve the purpose on the artifact's metadata before sharing,
      // so the World's observation trail carries the reviewer's intent
      // with the artifact itself.
      if (purpose) {
        try {
          const nextMeta = { ...(pending.artifact.metadata || {}), purpose };
          await api.patch(
            `/workspace/sessions/${pending.sessionId}/artifacts/${pending.artifact.id}`,
            { metadata: nextMeta },
          );
        } catch (e) { /* non-fatal */ }
      }
      // /worlds/{world_id}/observe — kicks off candidate extraction.
      await api.post(`/worlds/${vessel.world_id}/observe`, {
        session_id: pending.sessionId,
        artifact_id: pending.artifact.id,
      });
      toast.success("Shared with Payroll review.");
      nav("/payroll/prepare");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed.");
    } finally {
      setSharing(false);
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    // Include a short attachment mention so the AI has context.
    let contextualText = text;
    if (pending) {
      contextualText = `${text}\n\n[Attached: ${pending.artifact.title} (${fileTypeLabel(pending.artifact.title)})${purpose ? " — " + purpose : ""}]`;
    }
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: "user", text, attachment: pending?.artifact ? { title: pending.artifact.title, id: pending.artifact.id, purpose } : null }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: contextualText, history, context: getPageContext() }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const chunk of parts) {
          const line = chunk.replace(/^data:\s*/, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const payload = JSON.parse(line);
            if (payload.delta) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + payload.delta };
                return copy;
              });
            }
            if (payload.error) {
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", text: "Sorry — I hit a snag reaching my thinking. Try again in a moment." };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", text: "Sorry — I couldn't reach my thinking just now." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <Topbar title="SONMI" />
      <div className="flex-1 overflow-y-auto px-4 sm:px-10 pb-4 sm:pb-6">
        <div className="max-w-2xl mx-auto space-y-8" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="stagger-fade">
              <div className="flex items-center gap-3 mb-4">
                <TotoAvatar state="curious" size={44} className="text-stone-800" />
                <div className="label-eyebrow">A quiet companion</div>
              </div>
              <h2 className="font-serif-display text-3xl text-stone-900 leading-snug" data-testid="sonmi-greeting">
                Hi {user?.name?.split(" ")[0]}. What would help you today?
              </h2>
              <p className="text-stone-500 mt-3 max-w-lg leading-relaxed">
                Ask SONMI. She can draft messages, summarize pulse data, prepare 1:1 talking points,
                and think through leadership moments with you — and she can accept documents you'd like
                her to see. Try one of these:
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`suggestion-${i}`}
                    className="text-left px-5 py-4 rounded-2xl bg-white subtle-border text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="mt-6 text-right">
                <a href="/toto/assets" data-testid="link-customize-sonmi" className="label-eyebrow text-stone-500 hover:text-stone-900 underline underline-offset-4">
                  Customize SONMI →
                </a>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`msg-${m.role}-${i}`}>
              {m.role === "assistant" ? (
                <div className="max-w-[90%]">
                  <div className="label-eyebrow mb-2 flex items-center gap-2">
                    <TotoAvatar state={stateFor(m, i)} size={22} className="text-stone-700 transition-all duration-500" />
                    <span>SONMI</span>
                  </div>
                  <div className="text-stone-800 leading-relaxed whitespace-pre-wrap">
                    {m.text || <span className="text-stone-300 animate-pulse">thinking…</span>}
                  </div>
                </div>
              ) : (
                <div className="max-w-[85%] flex flex-col items-end gap-1">
                  <div className="bg-stone-100 rounded-2xl px-5 py-3 text-sm text-stone-800 whitespace-pre-wrap">{m.text}</div>
                  {m.attachment && (
                    <div className="text-[11px] text-stone-500 italic flex items-center gap-1.5" data-testid={`msg-attachment-${i}`}>
                      <FileText size={12} strokeWidth={1.5} />
                      {m.attachment.title}
                      {m.attachment.purpose ? ` — ${m.attachment.purpose}` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Attachment preview above composer */}
      {pending && (
        <div className="border-t border-stone-100 px-4 sm:px-10 pt-3">
          <div className="max-w-2xl mx-auto rounded-xl border border-stone-200 bg-stone-50 p-3" data-testid="pending-attachment">
            <div className="flex items-start gap-3">
              <FileText size={18} strokeWidth={1.5} className="text-stone-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-stone-900 truncate" data-testid="pending-attachment-name">
                  {pending.artifact.title}
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5" data-testid="pending-attachment-type">
                  {fileTypeLabel(pending.artifact.title)} · attached to session
                </div>
              </div>
              <button
                onClick={clearAttachment}
                data-testid="pending-attachment-remove"
                className="text-stone-400 hover:text-stone-700"
                title="Remove attachment"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <textarea
              rows={1}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="What is this document? (e.g. 'Canon draft for the July Google Reviews Understanding.')"
              className="mt-2 w-full text-[12px] bg-transparent border-t border-stone-200 pt-2 outline-none resize-none text-stone-700 placeholder:text-stone-400"
              data-testid="pending-attachment-purpose"
            />
            <div className="mt-2 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">
                Narrative evidence · will not create payroll entries
              </div>
              <button
                type="button"
                onClick={shareWithPayrollVessel}
                disabled={sharing}
                data-testid="btn-share-with-payroll-vessel"
                className="text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-md border border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
              >
                {sharing ? "Sharing…" : "Share with Payroll Vessel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-10 pb-6 sm:pb-8 pt-2 border-t border-stone-100">
        <div className="max-w-2xl mx-auto flex items-end gap-2 bg-white subtle-border rounded-full pl-2 pr-2 py-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTS.join(",")}
            onChange={onFileChosen}
            className="hidden"
            data-testid="chat-file-input"
          />
          <button
            type="button"
            onClick={onAttachClick}
            disabled={uploading}
            data-testid="btn-attach"
            title={`Add artifact (${ACCEPTED_EXTS.join(", ")})`}
            className="w-9 h-9 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-900 hover:bg-stone-100 disabled:opacity-30 transition-colors"
          >
            <Paperclip size={16} strokeWidth={1.5} />
          </button>
          <textarea
            data-testid="chat-input"
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Ask SONMI…"
            className="flex-1 bg-transparent outline-none text-sm text-stone-800 placeholder:text-stone-400 py-2 resize-none max-h-32"
          />
          <button
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            data-testid="btn-send"
            className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center disabled:opacity-30 hover:bg-stone-800 transition-colors"
          >
            <Send size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="max-w-2xl mx-auto text-center label-eyebrow mt-3">GPT-5.2 · A companion, not a replacement.</div>
      </div>
    </div>
  );
}
